// The Reviews page renders from one cached list (stores/reviews.js): every
// filter, dropdown and count below is derived from it locally, so changing a
// filter never goes back to the server. No imports — scripts/test-review-filters.mjs
// loads this file as a standalone module.

// Fetches the whole list in chunks of `size`, newest id first. `getChunk`
// receives {limit, before} and resolves to the endpoint's {reviews, next}.
export async function fetchReviewChunks(getChunk, size) {
  const all = [];
  let before = null;
  do {
    const chunk = await getChunk({ limit: size, before });
    all.push(...chunk.reviews);
    before = chunk.next;
  } while (before);
  return all;
}

// Groups rows under `key`, counting them and remembering the newest review.
function group(reviews, key, title) {
  const groups = new Map();
  for (const review of reviews) {
    const id = review[key];
    const entry = groups.get(id);
    if (entry) {
      entry.count += 1;
      entry.last = Math.max(entry.last, review.created);
    } else {
      groups.set(id, { id, title: review[title], count: 1, last: review.created });
    }
  }
  return [...groups.values()];
}

const newestFirst = (a, b) => b.last - a.last;
const strip = ({ id, title, count }) => ({ id, title, count });

// Bedrijfspagina's with reviews, oldest page first, as the old /pages read.
export function reviewPages(reviews) {
  return group(reviews, "page", "page_title")
    .sort((a, b) => a.id - b.id)
    .map(({ id, title, count }) => ({ id, companyname: title, count }));
}

// The activities of a page (narrowed to a location when one is chosen),
// the most recently reviewed first.
export function reviewActivities(reviews, { page, location = null }) {
  const scoped = reviews.filter(
    (r) => r.page === page && (location === null || r.location === location),
  );
  return group(scoped, "activity", "activity_title").sort(newestFirst).map(strip);
}

// The locations of a page (narrowed to an activity when one is chosen),
// the most recently reviewed first.
export function reviewLocations(reviews, { page, activity = null }) {
  const scoped = reviews.filter(
    (r) => r.page === page && (activity === null || r.activity === activity),
  );
  return group(scoped, "location", "location_title").sort(newestFirst).map(strip);
}

// The reviews the filters let through. `start`/`end` are epoch seconds and,
// as the server filter did, both exclusive.
export function filterReviews(reviews, { page = null, location = null, activity = null, start = null, end = null }) {
  return reviews.filter(
    (r) =>
      (page === null || r.page === page) &&
      (location === null || r.location === location) &&
      (activity === null || r.activity === activity) &&
      (start === null || r.created > start) &&
      (end === null || r.created < end),
  );
}

// Monorepo ADR-0105: an admin can leave either half of a review out of the score.
// The review stays in the list, but no average or count below includes that half.
export const countsForSpecialist = (review) => !review.specialist_exclusion;
export const countsForActivity = (review) => !review.activity_exclusion;
export const isExcluded = (review) => Boolean(review.activity_exclusion || review.specialist_exclusion);

// The Scorebord's freelancer rows, in specialist id order: averaged over the
// specialist halves that count. A specialist whose every half is excluded keeps
// the row with a null average and a count of 0 — "Nog geen reviews", not a 0.
export function specialistScores(reviews) {
  const groups = new Map();
  for (const review of reviews) {
    let entry = groups.get(review.specialist);
    if (!entry) {
      entry = { specialist: review.specialist, specialist_name: review.specialist_name, total: 0, review_count: 0 };
      groups.set(review.specialist, entry);
    }
    if (countsForSpecialist(review)) {
      entry.total += review.specialist_score;
      entry.review_count += 1;
    }
  }
  return [...groups.values()]
    .sort((a, b) => a.specialist - b.specialist)
    .map(({ total, ...entry }) => ({
      ...entry,
      average_score: entry.review_count ? total / entry.review_count : null,
    }));
}

// An activity's average over the activity halves that count; null without any.
export function activityScore(reviews, activity) {
  const counted = reviews.filter((r) => r.activity === activity && countsForActivity(r));
  return {
    average: counted.length ? counted.reduce((sum, r) => sum + r.activity_score, 0) / counted.length : null,
    count: counted.length,
  };
}
